import * as React from "react";
import { Button } from "../../Button";
import { z } from "zod";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Autocomplete, Option, Dialog, DialogActions, DialogContent, DialogTitleWithCloseButton, ConfirmationDialog } from "../../../../shared/components";

interface CommitDialogProps {
    entityName: string;
    entities: Option[];
    acceptCallback: (args: Object) => void;
    closeCallback: () => void;
}

function CommitDialog(props: CommitDialogProps) {
    const { entityName, entities, acceptCallback, closeCallback } = props;
    
    const schema = z.object({
        entity: z.object({
            value: z.string(),
            label: z.string()
        }, {
            error: `Please select a ${entityName} to commit.`
        })
    });
    type FormValues = z.infer<typeof schema>;

    const { handleSubmit, control } = useForm({ resolver: zodResolver(schema), defaultValues: { entity: null } });

    const [onClearCallback, setOnClearCallback] = React.useState<() => void>();
    const [confirmationLabel, setConfirmationLabel] = React.useState("");
    const [showConfirmation, setShowConfirmation] = React.useState(false);

    const onSubmit: SubmitHandler<FormValues> = formModel => {
        const onClearCallback = () => acceptCallback({
            entity_id: formModel.entity.value,
            entity_label: formModel.entity.label
        });
        setOnClearCallback(() => onClearCallback);
        setConfirmationLabel(formModel.entity.label);
        setShowConfirmation(true);
    };

    return (
        <>
            <Dialog
                open={true}
                onClose={(_event, reason) => {
                    if (reason !== 'backdropClick') {
                        closeCallback();
                    }
                }}
                disablePortal={true}
                aria-labelledby="form-dialog-title">
                <DialogTitleWithCloseButton onClose={closeCallback} id="form-dialog-title">
                    Commit {entityName}
                </DialogTitleWithCloseButton>
                <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                    <DialogContent>
                        <div className="vertical-form-field">
                            Select {entityName} to commit:
                        </div>
                        <Controller
                            control={control}
                            name="entity"
                            render={({
                                field: { value, onChange },
                                fieldState: { error }
                            }) => (
                                <Autocomplete
                                    value={value}
                                    onChange={(_event, option, _reason) => onChange((option as Option))}
                                    error={!!error}
                                    helperText={error?.message}
                                    isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
                                    getOptionLabel={(option: any) => option?.label || ""}
                                    size="small"
                                    fullWidth
                                    options={entities}
                                    disableClearable
                                />
                            )}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button type="submit" variant="text" color="success">
                            Commit
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {
                showConfirmation &&
                <ConfirmationDialog
                    title={`Commit ${entityName}?`}
                    open={showConfirmation}
                    setOpen={setShowConfirmation}
                    onConfirm={onClearCallback}
                >
                    <h3>Warning:</h3>
                        <br />
                        This operation will submit all changes made to '{confirmationLabel}' back to the Source {entityName} and delete this {entityName}.  This cannot be reversed!
                    <br />
                    <br />
                </ConfirmationDialog>
            }
        </>
    );
}

export { CommitDialog };